import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Clock, Edit2, Trash2, X, CheckCircle2, Save, Coffee, MessageSquare, Lock, FileDown, Mail, ShieldAlert, List, Timer, AlertTriangle, Banknote, Pencil, Users } from 'lucide-react';
import { stripDiacritics, csrf, barvaSmen, fmtH, fmtKc } from './spolecne';
import ListToolbar, { PohledPill } from '../../components/layout/ListToolbar';
import ModalShell from '../../components/ui/ModalShell';
import useTahMesicem from '../../hooks/useTahMesicem';
import OpravaVyplatyModal from './OpravaVyplatyModal';

// ── Docházka sub-tab ──
export default function Dochazka({ isDark, glassCard, mutedColor, iStyle, isAdmin = false, isSuperadmin = false, plnaVyska = false }) {
    const [records, setRecords] = useState([]);
    const [aktivni, setAktivni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mesic, setMesic] = useState(() => {
        const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    });
    const [editId, setEditId] = useState(null);
    // Oprava vyplacené směny (úvazek, záloha, navázání ruční výplaty) — vedení
    const [opravaId, setOpravaId] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [filterJmeno, setFilterJmeno] = useState('');
    // Pohled = předdefinovaná sada záznamů, vždy právě jeden (norma `ListToolbar`).
    const [pohled, setPohled] = useState('vse');
    // Záznamy vs. souhrn mezd po lidech — souhrn se přistěhoval ze zrušené
    // záložky Náklady. Patří sem: jsou tu táž data i uzávěrka, ze které se posílá
    // účetní, takže člověk nemusí skákat mezi dvěma obrazovkami.
    const [zobrazeni, setZobrazeni] = useState('zaznamy');
    const [laborCost, setLaborCost] = useState(null);
    // Pozdní zahájení schválená PINem — dřív vlastní záložka „PIN přepustky“.
    // Klíčem je `smena_id`, aby se daly přilepit k řádku docházky.
    const [prepustky, setPrepustky] = useState({});
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 900);
    const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 1200);

    useEffect(() => {
        const fn = () => {
            setIsMobile(window.innerWidth < 900);
            setIsNarrow(window.innerWidth < 1200);
        };
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);

    // Uzávěrky — keyed by `${rok}-${mesic}` pro O(1) lookup. Lokek čerpáme
    // primárně z odpovědi GET /api/dochazka/uzavirky?rok=…
    const [uzavirky, setUzavirky] = useState({});
    const [uzavirkyLoading, setUzavirkyLoading] = useState(false);
    const [closeModal, setCloseModal] = useState(null);          // { rok, mesic } when confirm dialog open
    const [closing, setClosing] = useState(false);
    const [closeError, setCloseError] = useState(null);
    const [reopenModal, setReopenModal] = useState(null);        // { rok, mesic }
    const [reopenDuvod, setReopenDuvod] = useState('');
    const [reopening, setReopening] = useState(false);
    const [reopenError, setReopenError] = useState(null);

    const SECTION_COLOR = barvaSmen(isDark);
    const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';

    const fetchData = useCallback(() => {
        setLoading(true);
        const [y, m] = mesic.split('-').map(Number);
        const datumOd = `${y}-${String(m).padStart(2,'0')}-01`;
        const lastDay = new Date(y, m, 0).getDate();
        const datumDo = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;

        Promise.all([
            fetch(`/api/dochazka?datum_od=${datumOd}&datum_do=${datumDo}`).then(r => r.json()),
            fetch('/api/dochazka/aktivni').then(r => r.json()),
            fetch(`/api/shifts/pin-prepustky?od=${datumOd}&do=${datumDo}`)
                .then(r => (r.ok ? r.json() : []))
                .catch(() => []),
            fetch(`/api/shifts/labor-cost?od=${datumOd}&do=${datumDo}`)
                .then(r => (r.ok ? r.json() : null))
                .catch(() => null),
        ]).then(([recs, akt, pin, mzdy]) => {
            setLaborCost(mzdy);
            setRecords(recs);
            setAktivni(akt);
            const mapa = {};
            (Array.isArray(pin) ? pin : []).forEach(p => { mapa[p.smena_id] = p; });
            setPrepustky(mapa);
        }).finally(() => setLoading(false));
    }, [mesic]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Načti uzávěrky pro aktuální rok (lock indikátor + state pro tlačítka)
    const fetchUzavirky = useCallback((rok) => {
        setUzavirkyLoading(true);
        fetch(`/api/dochazka/uzavirky?rok=${rok}`)
            .then(r => r.json())
            .then(list => {
                const map = {};
                (Array.isArray(list) ? list : []).forEach(u => { map[`${u.rok}-${u.mesic}`] = u; });
                setUzavirky(map);
            })
            .catch(() => {})
            .finally(() => setUzavirkyLoading(false));
    }, []);

    useEffect(() => {
        const [y] = mesic.split('-').map(Number);
        fetchUzavirky(y);
    }, [mesic, fetchUzavirky]);

    // Auto-refresh aktivních každých 60s
    useEffect(() => {
        const iv = setInterval(() => {
            fetch('/api/dochazka/aktivni').then(r => r.json()).then(setAktivni).catch(() => {});
        }, 60000);
        return () => clearInterval(iv);
    }, []);

    // Aktuálně zobrazený měsíc (pro lock check)
    const [aktualniRok, aktualniMesic] = mesic.split('-').map(Number);
    const aktualniLock = uzavirky[`${aktualniRok}-${aktualniMesic}`] || null;

    const submitClose = async () => {
        if (!closeModal) return;
        setClosing(true);
        setCloseError(null);
        try {
            const res = await fetch('/api/dochazka/uzavirky', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
                body: JSON.stringify({ rok: closeModal.rok, mesic: closeModal.mesic }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (data?.neukoncene?.length) {
                    setCloseError(`Nelze uzavřít — ${data.neukoncene.length}× neukončená směna: ` +
                        data.neukoncene.map(n => `${n.zamestnanec} (${n.datum})`).join(', '));
                } else {
                    setCloseError(data?.error || `Chyba ${res.status}`);
                }
                return;
            }
            setCloseModal(null);
            fetchUzavirky(closeModal.rok);
            fetchData();
        } finally { setClosing(false); }
    };

    const submitReopen = async () => {
        if (!reopenModal) return;
        if (reopenDuvod.trim().length < 5) {
            setReopenError('Důvod musí mít aspoň 5 znaků.');
            return;
        }
        setReopening(true);
        setReopenError(null);
        try {
            const res = await fetch(`/api/dochazka/uzavirky/${reopenModal.rok}/${reopenModal.mesic}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
                body: JSON.stringify({ duvod: reopenDuvod.trim() }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setReopenError(data?.error || `Chyba ${res.status}`);
                return;
            }
            setReopenModal(null);
            setReopenDuvod('');
            fetchUzavirky(reopenModal.rok);
        } finally { setReopening(false); }
    };

    const fmtTime = (dt) => {
        if (!dt) return '—';
        const s = String(dt);
        if (!s.includes('T') && s.includes(':')) return s.substring(0, 5);
        const d = new Date(s);
        if (isNaN(d)) return s.substring(0, 5);
        return d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    };

    const fmtDatum = (d) => {
        const dt = new Date(d + 'T12:00:00');
        return dt.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
    };

    const initials = (j, p) => `${j?.[0] ?? ''}${p?.[0] ?? ''}`.toUpperCase();

    const startEdit = (rec) => {
        setEditId(rec.id);
        setEditData({
            clock_in: rec.clock_in ? rec.clock_in.substring(0, 16) : '',
            clock_out: rec.clock_out ? rec.clock_out.substring(0, 16) : '',
            poznamka: rec.poznamka || '',
            castka_zaloha_kc: rec.castka_zaloha_kc ?? '',
            zaloha_locked: !!rec.zaloha_vyplacena_at, // vyplacenou zálohu nejde měnit
        });
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            const body = {
                clock_in: editData.clock_in || null,
                clock_out: editData.clock_out || null,
                poznamka: editData.poznamka || null,
            };
            // Zálohu posíláme jen když není vyplacená (jinak by ji backend odmítl)
            if (!editData.zaloha_locked) {
                body.castka_zaloha_kc = editData.castka_zaloha_kc === '' ? null : Number(editData.castka_zaloha_kc);
            }
            const res = await fetch(`/api/dochazka/${editId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
                body: JSON.stringify(body),
            });
            if (res.ok) { setEditId(null); fetchData(); }
            else { const j = await res.json().catch(() => ({})); alert(j.error || 'Uložení selhalo'); }
        } finally { setSaving(false); }
    };

    const smazatZaznam = async (rec) => {
        let hlaska = `Opravdu smazat záznam docházky ${rec.jmeno || ''} ${rec.prijmeni || ''} (${rec.datum})? Tuto akci nelze vrátit.`;
        if (rec.zaloha_vyplacena_at && rec.castka_zaloha_kc) {
            hlaska += `\n\nVyplacená záloha ${rec.castka_zaloha_kc} Kč se v pokladně automaticky stornuje protipohybem, aby zůstatek seděl.`;
        }
        if (!confirm(hlaska)) return;
        const res = await fetch(`/api/dochazka/${rec.id}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
        });
        if (res.ok) {
            const j = await res.json().catch(() => ({}));
            if (j.storno?.pocet) alert(`Záznam smazán. V pokladně vytvořeno storno výplaty (${j.storno.castka} Kč).`);
            fetchData();
        } else { const j = await res.json().catch(() => ({})); alert(j.error || 'Smazání selhalo'); }
    };

    const pauzyLabel = (pauzy) => {
        if (!pauzy || pauzy.length === 0) return null;
        return pauzy.map((p, i) => {
            const potvrzena = !!p.dokonceno_at;
            return (
                <span key={i} title={`${p.nazev || 'Pauza'} · ${p.placena ? 'placená' : 'neplacená'}${potvrzena ? ' · potvrzena' : ' · nepotvrzena'}`} style={{
                    fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 6, whiteSpace: 'nowrap',
                    background: p.placena ? 'rgba(59,130,246,.12)' : 'rgba(239,68,68,.1)',
                    color: p.placena ? '#3B82F6' : '#EF4444',
                    marginRight: 3, marginBottom: 2,
                    opacity: potvrzena ? 1 : 0.6,
                    border: potvrzena ? '1px solid rgba(16,185,129,0.4)' : '1px solid transparent',
                }}>
                    {potvrzena ? <CheckCircle2 size={9} /> : <Coffee size={9} />}
                    {p.od}–{p.do}
                </span>
            );
        });
    };

    // Naformátuje čas z ISO (browser TZ aware) nebo HH:MM:SS string.
    const fmtCas = (dt) => {
        if (!dt) return null;
        const s = String(dt);
        if (!s.includes('T') && s.includes(':')) return s.substring(0, 5);
        const d = new Date(s);
        if (isNaN(d)) return s.substring(0, 5);
        return d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    };

    // Buňka s clock časem — hlavní čas + (jen u manuálně upraveného záznamu)
    // malé přeškrtnuté auto. Z běžné uzávěrky se auto čas nezobrazuje.
    const clockCell = (hlavni, auto, recUpraveno) => {
        const hlavniStr = fmtCas(hlavni);
        const autoStr = fmtCas(auto);
        const upraveno = recUpraveno && hlavniStr && autoStr && hlavniStr !== autoStr;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                <span style={{ color: hlavni ? 'var(--text-primary)' : mutedColor }}>{hlavniStr || '—'}</span>
                {upraveno && (
                    <span style={{ fontSize: 10, color: mutedColor, textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}
                        title="Automaticky vypočtený čas (přepsán manuálním zadáním)">
                        {autoStr}
                    </span>
                )}
            </div>
        );
    };

    // Formát data/času pro tooltip — datum a čas dohromady.
    const fmtDatumCas = (dt) => {
        if (!dt) return '';
        const d = new Date(dt);
        if (isNaN(d)) return String(dt);
        return d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const prevMesic = () => {
        const [y, m] = mesic.split('-').map(Number);
        const d = new Date(y, m - 2, 1);
        setMesic(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    };
    const nextMesic = () => {
        const [y, m] = mesic.split('-').map(Number);
        const d = new Date(y, m, 1);
        setMesic(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    };
    const mesicLabel = () => {
        const [y, m] = mesic.split('-').map(Number);
        return new Date(y, m - 1).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
    };

    // Mobil: hlavička stojí, roluje jen seznam a měsíc se mění dotažením
    // seznamu za okraj (stejně jako Plán) — výběr měsíce v hlavičce není.
    const tah = useTahMesicem({
        mesic, posun: (smer) => (smer < 0 ? prevMesic() : nextMesic()),
        aktivni: isMobile, barva: SECTION_COLOR, mutedColor, obnova: [records, loading],
    });

    // ── Hledání a pohledy ──
    // Pohled = předdefinovaná sada dat, filtr by byl vlastní zužení. Tady stačí
    // pohledy — docházka má málo dimenzí a všechny se vejdou do pilulek.
    const hledany = stripDiacritics(filterJmeno);
    const projdeHledanim = (r) => !hledany
        || stripDiacritics(`${r.jmeno || ''} ${r.prijmeni || ''}`).includes(hledany);

    const jePozdni = (r) => !!prepustky[r.smena_id];
    const jeNeukoncena = (r) => !r.clock_out;
    const maZalohu = (r) => Number(r.castka_zaloha_kc || 0) > 0;
    const jeUpravena = (r) => !!r.upraveno;

    const PRAVIDLA = {
        vse: () => true,
        neukoncene: jeNeukoncena,
        pozdni: jePozdni,
        zaloha: maZalohu,
        upravene: jeUpravena,
    };

    const hledane = records.filter(projdeHledanim);
    const pocty = {
        vse: hledane.length,
        neukoncene: hledane.filter(jeNeukoncena).length,
        pozdni: hledane.filter(jePozdni).length,
        zaloha: hledane.filter(maZalohu).length,
        upravene: hledane.filter(jeUpravena).length,
    };
    const visible = hledane.filter(PRAVIDLA[pohled] || PRAVIDLA.vse);

    // Akce uzávěrky patří do hlavičky vpravo, ne mezi data.
    const akceUzaverky = [];
    if (isAdmin && !aktualniLock) {
        akceUzaverky.push({
            id: 'uzavrit', label: 'Uzavřít měsíc', Icon: Lock,
            title: `Uzavřít ${mesicLabel()}`,
            onClick: () => { setCloseError(null); setCloseModal({ rok: aktualniRok, mesic: aktualniMesic }); },
        });
    }
    if (aktualniLock && isSuperadmin) {
        akceUzaverky.push({
            id: 'otevrit', label: 'Znovu otevřít', Icon: ShieldAlert,
            title: 'Znovu otevřít — vyžaduje důvod, zapsáno do auditu',
            onClick: () => { setReopenError(null); setReopenDuvod(''); setReopenModal({ rok: aktualniLock.rok, mesic: aktualniLock.mesic }); },
        });
    }

    const stavUzaverky = aktualniLock && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span title={`Uzavřeno ${new Date(aktualniLock.uzavreno_at).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · ${aktualniLock.uzavrel_jmeno || '—'}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 6,
                    background: 'rgba(107,114,128,.15)', color: '#6B7280', fontSize: 11, fontWeight: 600, cursor: 'help' }}>
                <Lock size={11} /> Uzavřeno
            </span>
            {aktualniLock.ma_zip && (
                <a href={`/api/dochazka/uzavirky/${aktualniLock.rok}/${aktualniLock.mesic}/zip`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 6,
                        background: 'rgba(99,102,241,.12)', color: '#6366F1', border: '1px solid rgba(99,102,241,.3)',
                        fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
                    title="Stáhnout ZIP s PDF za zaměstnance">
                    <FileDown size={12} /> ZIP
                </a>
            )}
            {aktualniLock.email_odeslano_at && (
                <span title={`Odesláno na ${aktualniLock.email_prijemce || '—'} dne ${new Date(aktualniLock.email_odeslano_at).toLocaleString('cs-CZ')}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 6,
                        background: 'rgba(16,185,129,.12)', color: '#10B981', fontSize: 11, fontWeight: 600, cursor: 'help' }}>
                    <Mail size={12} /> Email odeslán
                </span>
            )}
        </div>
    );

    // Odznak pozdního zahájení. Dřív to byla vlastní záložka „PIN přepustky“,
    // ve které se stejná směna hledala podruhé. Teď visí přímo u času příchodu
    // — vidím ho v každém pohledu, ne jen v tom jednom.
    const odznakPozdni = (rec, maly = false) => {
        const p = prepustky[rec.smena_id];
        if (!p) return null;
        const vazne = p.zpozdeni_min >= 120;
        return (
            <span title={`Pozdní zahájení o ${p.zpozdeni_min} min · odemkl ${p.schvalil || '—'}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                    padding: maly ? '1px 5px' : '1px 6px', borderRadius: 99, cursor: 'help',
                    fontSize: maly ? 9.5 : 10, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    background: vazne ? 'rgba(239,68,68,.15)' : 'rgba(245,158,11,.15)',
                    color: vazne ? '#EF4444' : '#F59E0B' }}>
                <AlertTriangle size={maly ? 8 : 9} />+{p.zpozdeni_min}
            </span>
        );
    };

    // ── Mobil: karta na řádek místo tabulky ──
    // Dvanáct sloupců se do 402 px nevejde a vodorovné rolování tabulky je na
    // dotyku slepá ulička. Karta drží to, proč se člověk dívá: kdo, kdy, kolik
    // a jestli s tím není něco v neřádu. Editace zůstává na desktopu — přepisovat
    // čas příchodu palcem nikdo nechce.
    const kartaZaznamu = (rec) => {
        const odchylka = rec.smena_hodin && rec.hodiny_odpracovane
            ? Number(rec.hodiny_odpracovane) - Number(rec.smena_hodin)
            : null;
        const radek = (popis, hodnota) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5 }}>
                <span style={{ color: mutedColor }}>{popis}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{hodnota}</span>
            </div>
        );
        return (
            // Na mobilu řádek přes celou šířku (edge to edge) oddělený linkou —
            // karta v kartě byla rámeček navíc. Barva šablony zůstává proužkem
            // u levé hrany displeje.
            <div key={rec.id} style={{
                padding: '12px 16px 12px 13px',
                borderBottom: `1px solid ${borderColor}`,
                borderLeft: `3px solid ${rec.sablon_barva || (jeNeukoncena(rec) ? '#F59E0B' : 'transparent')}`,
                display: 'flex', flexDirection: 'column', gap: 7,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {rec.avatar_url ? (
                        <img src={rec.avatar_url} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', objectPosition: rec.avatar_position, flexShrink: 0 }} />
                    ) : (
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#EEEDFE', color: '#3C3489', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 700, flexShrink: 0 }}>
                            {initials(rec.jmeno, rec.prijmeni)}
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {rec.jmeno} {rec.prijmeni}
                        </div>
                        <div style={{ fontSize: 10.5, color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtDatum(rec.datum)}{rec.sablon_nazev ? ` · ${rec.sablon_nazev}` : ''}
                        </div>
                    </div>
                    {odznakPozdni(rec, true)}
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtCas(rec.clock_in) || '—'}–{fmtCas(rec.clock_out) || <span style={{ color: '#F59E0B' }}>běží</span>}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: SECTION_COLOR, fontVariantNumeric: 'tabular-nums' }}>
                        {rec.hodiny_odpracovane ? fmtH(rec.hodiny_odpracovane) : '—'}
                    </span>
                    {odchylka !== null && Math.abs(odchylka) >= 0.25 && (
                        <span style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                            color: odchylka > 0 ? '#EF4444' : '#10B981' }}>
                            {odchylka > 0 ? '+' : '−'}{fmtH(Math.abs(odchylka))}
                        </span>
                    )}
                </div>

                {(rec.smena_cas_od || rec.sazba_label) && radek(
                    'Plán',
                    `${rec.smena_cas_od ? `${String(rec.smena_cas_od).slice(0, 5)}–${String(rec.smena_cas_do).slice(0, 5)}` : '—'}${rec.sazba_label ? ` · ${rec.sazba_label}` : ''}`,
                )}
                {Number(rec.castka_zaloha_kc || 0) > 0 && radek('Záloha', `${Math.round(rec.castka_zaloha_kc)} Kč`)}
                {Number(rec.dysko_kc || 0) > 0 && radek('Dýško', `${Math.round(rec.dysko_kc)} Kč`)}

                {(rec.upraveno || rec.poznamka) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 10.5, color: mutedColor }}>
                        {rec.upraveno && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 99,
                                background: rec.souhlas ? 'rgba(16,185,129,.14)' : 'rgba(245,158,11,.14)',
                                color: rec.souhlas ? '#10B981' : '#F59E0B', fontWeight: 600 }}>
                                <Pencil size={8} />{rec.souhlas ? 'upraveno · odsouhlaseno' : 'upraveno'}
                            </span>
                        )}
                        {rec.poznamka && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                                <MessageSquare size={9} style={{ flexShrink: 0 }} />
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rec.poznamka}</span>
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ── Souhrn mezd po lidech ──
    // Dřív vlastní záložka Náklady. Souhrn období (hodiny, FTE, plán vs. skutečnost)
    // už kreslí panel „Období“ v Plánu — tady zůstává jen to, co panel neumí:
    // **kdo nás kolik stál**.
    // Souhrn se kreslí **uvnitř** karty záznamů, takže nesmí použít `glassCard` —
    // sklo v skle skládá průsvit i blur dvakrát a plocha zmléční.
    const plochaVKarte = {
        background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-elevated)',
        border: `1px solid ${borderColor}`,
    };

    const perZam = laborCost?.per_zamestnanec || [];
    const celkem = laborCost?.celkem || {};

    // Barva skutečnosti proti plánu: nad plán = přečerpáno, pod = úspora.
    const barvaOdchylky = (plan, skut) => Number(skut) > Number(plan) + 0.5 ? '#EF4444'
        : Number(skut) < Number(plan) - 0.5 ? '#10B981' : mutedColor;

    const souhrnMezd = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12,
            ...(plnaVyska ? { flex: 1, minHeight: 0, overflowY: 'auto' } : null) }}>
            {perZam.length === 0 ? (
                <div style={{ ...plochaVKarte, borderRadius: 10, padding: 30, textAlign: 'center', color: mutedColor, fontSize: 13 }}>
                    V tomhle měsíci není co počítat.
                </div>
            ) : isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {perZam.map(z => (
                        <div key={z.zamestnanec_id} style={{ ...plochaVKarte, borderRadius: 11, padding: '10px 13px',
                            display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>
                                    {z.zamestnanec}
                                </span>
                                <span style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99,
                                    background: z.typ === 'fix' ? 'rgba(16,185,129,.14)' : `${SECTION_COLOR}20`,
                                    color: z.typ === 'fix' ? '#10B981' : SECTION_COLOR }}>
                                    {z.typ === 'fix' ? 'Fix' : 'Var'}
                                </span>
                            </div>
                            {[
                                ['Hodin', fmtH(z.hodiny_plan), fmtH(z.hodiny_skut), z.hodiny_plan, z.hodiny_skut],
                                ['Čistá', fmtKc(z.cista_plan), fmtKc(z.cista_skut), z.cista_plan, z.cista_skut],
                                ['S odvody', fmtKc(z.odvody_plan), fmtKc(z.odvody_skut), z.odvody_plan, z.odvody_skut],
                            ].map(([popis, plan, skut, pRaw, sRaw]) => (
                                <div key={popis} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5 }}>
                                    <span style={{ color: mutedColor }}>{popis}</span>
                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                        <span style={{ color: mutedColor }}>{plan || '–'}</span>
                                        <span style={{ color: mutedColor, margin: '0 5px' }}>→</span>
                                        <span style={{ fontWeight: 600, color: barvaOdchylky(pRaw, sRaw) }}>{skut || '–'}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ ...plochaVKarte, borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    ...(plnaVyska ? { flex: 1, minHeight: 0 } : null) }}>
                    <div style={{ overflow: 'auto', ...(plnaVyska ? { flex: 1, minHeight: 0 } : null) }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead>
                                <tr>
                                    {['Zaměstnanec', 'Typ', 'Hodin plán', 'Hodin skut.', 'Čistá plán', 'Čistá skut.', 'S odv. plán', 'S odv. skut.'].map((h, j) => (
                                        <th key={h} style={{ textAlign: j < 2 ? 'left' : 'right', padding: '9px 12px', fontSize: 10,
                                            fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap',
                                            position: 'sticky', top: 0, zIndex: 1,
                                            background: isDark ? '#16141f' : 'var(--surface-elevated)',
                                            boxShadow: `inset 0 -1px 0 ${borderColor}` }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {perZam.map(z => {
                                    const cell = { textAlign: 'right', padding: '8px 12px', fontVariantNumeric: 'tabular-nums' };
                                    return (
                                        <tr key={z.zamestnanec_id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                                            <td style={{ padding: '8px 12px', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                                                {z.zamestnanec}
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>
                                                <span style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99,
                                                    background: z.typ === 'fix' ? 'rgba(16,185,129,.14)' : `${SECTION_COLOR}20`,
                                                    color: z.typ === 'fix' ? '#10B981' : SECTION_COLOR }}>
                                                    {z.typ === 'fix' ? 'Fix' : 'Var'}
                                                </span>
                                            </td>
                                            <td style={{ ...cell, color: mutedColor }}>{fmtH(z.hodiny_plan) || '–'}</td>
                                            <td style={{ ...cell, fontWeight: 600, color: barvaOdchylky(z.hodiny_plan, z.hodiny_skut) }}>{fmtH(z.hodiny_skut) || '–'}</td>
                                            <td style={{ ...cell, color: mutedColor }}>{fmtKc(z.cista_plan) || '–'}</td>
                                            <td style={{ ...cell, fontWeight: 600, color: barvaOdchylky(z.cista_plan, z.cista_skut) }}>{fmtKc(z.cista_skut) || '–'}</td>
                                            <td style={{ ...cell, color: mutedColor }}>{fmtKc(z.odvody_plan) || '–'}</td>
                                            <td style={{ ...cell, fontWeight: 600, color: barvaOdchylky(z.odvody_plan, z.odvody_skut) }}>{fmtKc(z.odvody_skut) || '–'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div style={{ fontSize: 11, color: mutedColor, lineHeight: 1.5 }}>
                Čísla jsou řídicí odhad (sazba × hodiny + odvody), ne výplatní podklad —
                ten posílá uzávěrka měsíce s hodinami a zálohami.
            </div>
        </div>
    );

    const prepinacZobrazeni = (
        <div style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 7,
            background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
            {[{ id: 'zaznamy', label: 'Záznamy', Ikona: List }, { id: 'souhrn', label: 'Souhrn mezd', Ikona: Users }].map(v => (
                <button key={v.id} onClick={() => setZobrazeni(v.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 6,
                    border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
                    background: zobrazeni === v.id ? `${SECTION_COLOR}20` : 'transparent',
                    color: zobrazeni === v.id ? SECTION_COLOR : mutedColor,
                }}>
                    <v.Ikona size={12} />{v.label}
                </button>
            ))}
        </div>
    );

    const navigaceMesice = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button onClick={prevMesic} title="Předchozí měsíc" aria-label="Předchozí měsíc"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4, display: 'flex' }}>
                <ChevronLeft size={15} />
            </button>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: 104,
                textAlign: 'center', textTransform: 'capitalize' }}>{mesicLabel()}</span>
            <button onClick={nextMesic} title="Další měsíc" aria-label="Další měsíc"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4, display: 'flex' }}>
                <ChevronRight size={15} />
            </button>
        </div>
    );

    const pilulka = (id, Ikona, label, upozorneni = false) => (
        <PohledPill key={id} Icon={Ikona} label={label} pocet={pocty[id]}
            aktivni={pohled === id} upozorneni={upozorneni} onClick={() => setPohled(id)}
            isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={isMobile} />
    );

    // ── Pravý sloupec ──
    // „Právě na směně“ bývalo široká karta nad tabulkou, která brála výšku a většinu
    // dne hlásila „nikdo“. Teď stojí ve sloupci vedle dat, kde má místo a nikomu
    // nepřekáží — a pod ní čísla období, která se mění podle zobrazení.
    const panelDochazky = (kompakt = false) => {
        const nadpisSekce = (text) => (
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
                color: mutedColor, ...(kompakt ? null : { margin: '14px 0 5px' }) }}>{text}</div>
        );
        const radek = (popis, hodnota, barva, titulek) => (
            <div title={titulek} style={kompakt ? {
                display: 'inline-flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap',
            } : {
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
                padding: '7px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)'}`,
            }}>
                <span style={{ fontSize: 12, color: mutedColor }}>{popis}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                    color: barva || 'var(--text-primary)' }}>{hodnota}</span>
            </div>
        );
        const upozorneni = (barva, titulek, detail) => (
            <div title={kompakt ? detail : undefined} style={{ borderRadius: 9, background: `${barva}14`,
                border: `1px solid ${barva}38`, fontSize: 11.5, lineHeight: 1.45, color: barva,
                ...(kompakt ? { padding: '4px 9px', whiteSpace: 'nowrap' } : { padding: '8px 10px', marginTop: 7 }) }}>
                <b style={kompakt ? undefined : { display: 'block', marginBottom: 1 }}>{titulek}</b>
                {!kompakt && detail}
            </div>
        );

        // Čísla z docházky za měsíc — počítají se ze **všech** záznamů, ne z toho,
        // co je zrovna vyfiltrované: panel mluví o měsíci, ne o výběru.
        const odpracovano = records.reduce((a, r) => a + Number(r.hodiny_odpracovane || 0), 0);
        const zaloh = records.filter(r => Number(r.castka_zaloha_kc || 0) > 0);
        const zalohyCelkem = zaloh.reduce((a, r) => a + Number(r.castka_zaloha_kc || 0), 0);
        const cekaNaVyplaceni = zaloh.filter(r => !r.zaloha_vyplacena_at).length;
        const dyska = records.reduce((a, r) => a + Number(r.dysko_kc || 0), 0);
        const bezSouhlasu = records.filter(r => r.upraveno && !r.souhlas).length;
        const neukoncene = records.filter(r => !r.clock_out).length;
        const pozdni = records.filter(r => prepustky[r.smena_id]).length;

        return (
            <div style={{ ...glassCard, borderRadius: 12, display: 'flex',
                ...(kompakt
                    ? { padding: '10px 14px', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', flexShrink: 0 }
                    : { padding: 14, flexDirection: 'column',
                        ...(plnaVyska ? { flex: 1, minHeight: 0, overflowY: 'auto' } : null) }) }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0,
                    ...(kompakt ? null : { marginBottom: 9 }) }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: aktivni.length ? '#1D9E75' : mutedColor,
                        ...(aktivni.length ? { animation: 'smena-pulse 2s ease-in-out infinite' } : null) }} />
                    <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: mutedColor }}>
                        Právě na směně · {aktivni.length}
                    </span>
                </div>

                {aktivni.length === 0 ? (
                    <div style={{ fontSize: 11.5, color: mutedColor, lineHeight: 1.5 }}>Nikdo právě nejede.</div>
                ) : (
                    <div style={{ display: 'flex', gap: 6,
                        ...(kompakt ? { flexWrap: 'wrap' } : { flexDirection: 'column' }) }}>
                        {aktivni.map(a => {
                            // Obrana proti float diffInMinutes z backendu — ať máme jistotu celých čísel.
                            const totalMin = Math.max(0, Math.floor(Number(a.elapsed_min) || 0));
                            const h = Math.floor(totalMin / 60);
                            const m = totalMin % 60;
                            return (
                                <div key={a.zamestnanec_id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                    background: isDark ? 'rgba(255,255,255,.04)' : 'var(--surface-elevated)',
                                    border: `1px solid ${borderColor}`, borderRadius: 9, padding: '7px 9px' }}>
                                    {a.avatar_url ? (
                                        <img src={a.avatar_url} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', objectPosition: a.avatar_position, flexShrink: 0 }} />
                                    ) : (
                                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#EEEDFE', color: '#3C3489', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 700, flexShrink: 0 }}>
                                            {initials(a.jmeno, a.prijmeni)}
                                        </div>
                                    )}
                                    <div style={{ lineHeight: 1.3, minWidth: 0, flex: 1 }}>
                                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.jmeno} {a.prijmeni}</div>
                                        <div style={{ fontSize: 10, color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>
                                            od {fmtTime(a.clock_in)} · {h > 0 ? `${h}h ${m}m` : `${m}m`}
                                            {a.sablon_nazev && <span style={{ marginLeft: 4, color: a.sablon_barva || mutedColor }}>· {a.sablon_nazev}</span>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {nadpisSekce(mesicLabel())}

                {zobrazeni === 'souhrn' ? (<>
                    {radek('Lidí', perZam.length || '–')}
                    {radek('Hodin plán', fmtH(celkem.hodiny_plan) || '–')}
                    {radek('Odpracováno', fmtH(celkem.hodiny_skut) || '–',
                        barvaOdchylky(celkem.hodiny_plan, celkem.hodiny_skut))}
                    {radek('Čistá mzda', fmtKc(celkem.cista_plan) || '–', SECTION_COLOR, 'Plán')}
                    {radek('… skutečnost', fmtKc(celkem.cista_skut) || '–',
                        barvaOdchylky(celkem.cista_plan, celkem.cista_skut))}
                    {radek('S odvody', fmtKc(celkem.odvody_plan) || '–', SECTION_COLOR, 'Plán')}
                    {radek('… skutečnost', fmtKc(celkem.odvody_skut) || '–',
                        barvaOdchylky(celkem.odvody_plan, celkem.odvody_skut))}
                </>) : (<>
                    {radek('Záznamů', records.length || '–')}
                    {radek('Odpracováno', fmtH(odpracovano) || '–')}
                    {zalohyCelkem > 0 && radek('Zálohy', fmtKc(zalohyCelkem), null,
                        `${zaloh.length}×${cekaNaVyplaceni ? `, z toho ${cekaNaVyplaceni} čeká na vyplacení` : ''}`)}
                    {dyska > 0 && radek('Dýška', fmtKc(dyska), '#10B981')}
                </>)}

                {zobrazeni !== 'souhrn' && (neukoncene > 0 || pozdni > 0 || bezSouhlasu > 0 || cekaNaVyplaceni > 0) && (<>
                    {nadpisSekce('K dořešení')}
                    {neukoncene > 0 && upozorneni('#F59E0B', `Neukončené · ${neukoncene}`,
                        'chybí odchod — hodiny se nepočítají')}
                    {pozdni > 0 && upozorneni('#EF4444', `Pozdní příchody · ${pozdni}`,
                        'zahájeno na PIN manažera')}
                    {bezSouhlasu > 0 && upozorneni('#FF8A00', `Upraveno bez souhlasu · ${bezSouhlasu}`,
                        'zaměstnanec korekci neodsouhlasil')}
                    {cekaNaVyplaceni > 0 && upozorneni('#3B82F6', `Zálohy k vyplacení · ${cekaNaVyplaceni}`,
                        'potřebují potvrzení manažera')}
                </>)}
            </div>
        );
    };

    const zobrazitPanel = !isMobile && !isNarrow;

    // Mobil: seznam roluje sám pod stojící hlavičkou; nahoře název měsíce
    // (přepínač období v hlavičce není), dole výzva k tahu na další měsíc.
    const prazdno = (text) => <div style={{ textAlign: 'center', padding: 24, color: mutedColor, fontSize: 13 }}>{text}</div>;
    const mobilObsah = isMobile && (
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', ...(plnaVyska ? { flex: 1, minHeight: 0 } : null) }}>
            {tah.ukazatele}
            <div {...tah.seznamProps} style={{ ...(plnaVyska ? { flex: 1, minHeight: 0, overflowY: 'auto' } : null), ...tah.seznamStyl }}>
                <div ref={tah.obsahRef}>
                    <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'capitalize' }}>{mesicLabel()}</span>
                        {aktualniLock && <Lock size={14} style={{ color: '#10B981' }} aria-label="Měsíc je uzavřený" />}
                    </div>
                    {loading ? prazdno('Načítám…')
                        : zobrazeni === 'souhrn' ? <div style={{ padding: '0 16px' }}>{souhrnMezd}</div>
                        : records.length === 0 ? prazdno('Žádné záznamy v tomto měsíci.')
                        : visible.length === 0 ? prazdno(pohled === 'vse'
                            ? `Žádný zaměstnanec neodpovídá hledání „${filterJmeno}“.`
                            : 'V tomhle pohledu není žádný záznam — což je obvykle dobrá zpráva.')
                        : visible.map(kartaZaznamu)}
                    <div style={{ textAlign: 'center', fontSize: 11.5, color: mutedColor, padding: '14px 0 8px' }}>
                        Táhni dál pro {tah.sousedniMesic(1)}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', gap: 13, alignItems: 'stretch',
            ...(plnaVyska ? { flex: 1, minHeight: 0 } : null) }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16,
            ...(plnaVyska ? { minHeight: 0 } : null) }}>
            {/* Na užším okně se sloupec nevejde — panel klízne nad tabulku. */}
            {/* Na mobilu panel není: zabíral půl obrazovky a totéž, co hlásil
                (pozdní příchody, upravené), je v pohledech pod hledáním. */}
            {!zobrazitPanel && !isMobile && panelDochazky(true)}
            {/* ── ZÁZNAMY DOCHÁZKY ── */}
            {/* Karta vyplňuje zbytek stránky a roluje se uvnitř ní jen tabulka —
                hlavička s hledáním a pohledy zůstává na místě. */}
            <div style={{ ...(isMobile ? { margin: '0 -16px' } : { ...glassCard, borderRadius: 10, padding: 16 }),
                display: 'flex', flexDirection: 'column',
                ...(plnaVyska ? { flex: 1, minHeight: 0 } : null) }}>
                {/* Hlavička podle normy: hledání vlevo, akce vpravo, pohledy pod tím.
                    Uzávěrka je akce modulu, ne řádek mezi daty — a měla tyrkysovou
                    z jiné sekce, takže teď jede v akcentu Směn. */}
                <div style={{ ...(isMobile ? { marginBottom: 4 } : { marginBottom: 12, marginLeft: -16, marginRight: -16, marginTop: -16 }), flexShrink: 0 }}>
                    <ListToolbar
                        siroky={!isMobile} kompaktni={isMobile} sklo
                        isDark={isDark} mutedColor={mutedColor} inputStyle={iStyle} barva={SECTION_COLOR}
                        hledani={{
                            value: filterJmeno, onChange: setFilterJmeno,
                            placeholder: 'Hledat zaměstnance…', placeholderKratky: 'Hledat…',
                        }}
                        akce={akceUzaverky}
                        nastroje={stavUzaverky}
                        pohled={zobrazeni === 'zaznamy' ? (<>
                            {pilulka('vse', List, 'Vše')}
                            {pilulka('neukoncene', Timer, 'Neukončené', pocty.neukoncene > 0)}
                            {pilulka('pozdni', AlertTriangle, 'Pozdní příchody', pocty.pozdni > 0)}
                            {pilulka('zaloha', Banknote, 'Se zálohou')}
                            {pilulka('upravene', Pencil, 'Upravené')}
                        </>) : null}
                        pohledVpravo={isMobile ? null : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {navigaceMesice}{prepinacZobrazeni}
                            </div>
                        )}
                        pod={isMobile ? prepinacZobrazeni : null}
                    />
                </div>


                {isMobile ? mobilObsah : loading ? (
                    <div style={{ textAlign: 'center', padding: 24, color: mutedColor }}>Načítám...</div>
                ) : zobrazeni === 'souhrn' ? souhrnMezd : records.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24, color: mutedColor }}>Žádné záznamy v tomto měsíci.</div>
                ) : visible.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24, color: mutedColor, fontSize: 13 }}>
                        {pohled === 'vse'
                            ? `Žádný zaměstnanec neodpovídá hledání „${filterJmeno}“.`
                            : 'V tomhle pohledu není žádný záznam — což je obvykle dobrá zpráva.'}
                    </div>
                ) : isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column',
                        ...(plnaVyska ? { flex: 1, minHeight: 0, overflowY: 'auto' } : null) }}>
                        {visible.map(kartaZaznamu)}
                    </div>
                ) : (() => {
                    return (
                    <div style={{ overflow: 'auto', ...(plnaVyska ? { flex: 1, minHeight: 0 } : null) }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            {/* Záhlaví drží při rolování nahoře. Spodní linku kreslí `box-shadow`:
                                při `border-collapse` patří rámeček tabulce a odroloval by pryč. */}
                            <thead>
                                <tr>
                                    {['Datum', 'Zaměstnanec', 'Směna', 'Přestávky', 'Clock in', 'Clock out', 'Hodiny', 'Sazba', 'Záloha', 'Dýško', 'Poznámka', ''].map(h => (
                                        <th key={h} style={{ textAlign: 'left', padding: '8px 8px', fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap',
                                            position: 'sticky', top: 0, zIndex: 1,
                                            background: isDark ? '#14121c' : 'var(--surface-raised)',
                                            boxShadow: `inset 0 -1px 0 ${borderColor}` }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(rec => {
                                    const isEditing = editId === rec.id;
                                    return (
                                        <tr key={rec.id} style={{ borderBottom: `1px solid ${borderColor}`, transition: 'background 0.1s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,.02)' : 'rgba(0,0,0,.015)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{fmtDatum(rec.datum)}</td>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {rec.avatar_url ? (
                                                        <img src={rec.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', objectPosition: rec.avatar_position }} />
                                                    ) : (
                                                        <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#EEEDFE', color: '#3C3489', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600 }}>
                                                            {initials(rec.jmeno, rec.prijmeni)}
                                                        </div>
                                                    )}
                                                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{rec.jmeno} {rec.prijmeni}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                                                {rec.sablon_nazev && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, background: (rec.sablon_barva || '#666') + '22', color: rec.sablon_barva || mutedColor, fontWeight: 600, marginRight: 4 }}>{rec.sablon_nazev}</span>}
                                                <span style={{ color: mutedColor }}>{fmtTime(rec.smena_cas_od)}–{fmtTime(rec.smena_cas_do)}</span>
                                            </td>
                                            <td style={{ padding: '8px 8px' }}>{pauzyLabel(rec.pauzy) || <span style={{ color: mutedColor }}>—</span>}</td>
<td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                                                {isEditing ? (
                                                    <input type="datetime-local" value={editData.clock_in} onChange={e => setEditData(p => ({ ...p, clock_in: e.target.value }))} style={{ ...iStyle, width: 160, fontSize: 12, padding: '4px 6px' }} />
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                        {clockCell(rec.clock_in, rec.clock_in_auto, rec.upraveno)}
                                                        {odznakPozdni(rec)}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                                                {isEditing ? (
                                                    <input type="datetime-local" value={editData.clock_out} onChange={e => setEditData(p => ({ ...p, clock_out: e.target.value }))} style={{ ...iStyle, width: 160, fontSize: 12, padding: '4px 6px' }} />
                                                ) : (
                                                    clockCell(rec.clock_out, rec.clock_out_auto, rec.upraveno)
                                                )}
                                            </td>
                                            <td style={{ padding: '8px 8px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                                                {rec.hodiny_odpracovane ? `${rec.hodiny_odpracovane}h` : '—'}
                                                {rec.upraveno && <span style={{ marginLeft: 4, fontSize: 9, padding: '1px 5px', borderRadius: 5, background: 'rgba(251,191,36,.15)', color: '#D97706', fontWeight: 600 }}>upraveno</span>}
                                                {rec.souhlas && <span title="Zaměstnanec upravená čísla odsouhlasil při odhlášení" style={{ marginLeft: 4, fontSize: 9, padding: '1px 5px', borderRadius: 5, background: 'rgba(16,185,129,.15)', color: '#0F9D6B', fontWeight: 600 }}>odsouhlaseno</span>}
                                            </td>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', fontSize: 12 }}>
                                                {rec.sazba_label ? (
                                                    <span style={{
                                                        padding: '2px 8px', borderRadius: 5, fontWeight: 600,
                                                        background: rec.sazba_label === 'FIX' ? 'rgba(99,102,241,0.12)' : 'rgba(16,185,129,0.12)',
                                                        color: rec.sazba_label === 'FIX' ? '#6366F1' : '#10B981',
                                                    }}>
                                                        {rec.sazba_label}
                                                    </span>
                                                ) : <span style={{ color: mutedColor }}>—</span>}
                                            </td>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', fontSize: 12 }}>
                                                {isEditing ? (
                                                    editData.zaloha_locked ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: 2 }}>
                                                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{rec.castka_zaloha_kc} Kč</span>
                                                            <span style={{ fontSize: 10, color: mutedColor }}>vyplaceno — nelze měnit</span>
                                                        </div>
                                                    ) : (
                                                        <input type="number" min="0" value={editData.castka_zaloha_kc}
                                                            onChange={e => setEditData(p => ({ ...p, castka_zaloha_kc: e.target.value }))}
                                                            placeholder="Kč" style={{ ...iStyle, width: 90, fontSize: 12, padding: '4px 6px' }} />
                                                    )
                                                ) : rec.castka_zaloha_kc ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: 2 }}>
                                                        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                                                            {rec.castka_zaloha_kc} Kč
                                                        </span>
                                                        {rec.zaloha_vyplacena_at ? (
                                                            <span style={{ fontSize: 10, color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                                                title={`Potvrdil: ${rec.zaloha_potvrzena_by || '—'} · ${fmtDatumCas(rec.zaloha_vyplacena_at)}`}>
                                                                <CheckCircle2 size={10} /> {rec.zaloha_potvrzena_by || 'manažer'}
                                                                <span style={{ color: mutedColor, marginLeft: 2 }}>· {fmtDatumCas(rec.zaloha_vyplacena_at)}</span>
                                                            </span>
                                                        ) : (
                                                            <span style={{ fontSize: 10, color: '#F59E0B' }}>čeká na vyplacení</span>
                                                        )}
                                                    </div>
                                                ) : <span style={{ color: mutedColor }}>—</span>}
                                            </td>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap', fontSize: 12 }}>
                                                {rec.dysko_kc != null && Number(rec.dysko_kc) !== 0 ? (
                                                    <span style={{
                                                        fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                                        color: Number(rec.dysko_kc) >= 0 ? '#10B981' : '#EF4444',
                                                    }}>
                                                        {Number(rec.dysko_kc) >= 0 ? '+' : '−'}
                                                        {Math.abs(Math.round(rec.dysko_kc)).toLocaleString('cs-CZ')} Kč
                                                    </span>
                                                ) : <span style={{ color: mutedColor }}>—</span>}
                                            </td>
                                            <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                                                {isEditing ? (
                                                    <input type="text" value={editData.poznamka} onChange={e => setEditData(p => ({ ...p, poznamka: e.target.value }))} placeholder="Poznámka..." style={{ ...iStyle, width: '100%', fontSize: 12, padding: '4px 6px' }} />
                                                ) : rec.poznamka ? (
                                                    <span title={rec.poznamka}
                                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 5, color: SECTION_COLOR, cursor: 'help' }}>
                                                        <MessageSquare size={14} />
                                                    </span>
                                                ) : (
                                                    <span style={{ color: mutedColor, fontSize: 12 }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                        <button onClick={saveEdit} disabled={saving} style={{ background: SECTION_COLOR, color: '#fff', border: 'none', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                            <Save size={11} />{saving ? '...' : 'Uložit'}
                                                        </button>
                                                        <button onClick={() => setEditId(null)} style={{ background: 'none', border: `1px solid ${borderColor}`, borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11, color: mutedColor }}>×</button>
                                                    </div>
                                                ) : aktualniLock ? (
                                                    <span title="Měsíc je uzavřen — úpravy zablokovány" style={{ color: mutedColor, padding: 4, opacity: 0.4, display: 'inline-flex' }}>
                                                        <Lock size={13} />
                                                    </span>
                                                ) : (
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <button onClick={() => startEdit(rec)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4 }} title="Upravit">
                                                            <Edit2 size={13} />
                                                        </button>
                                                        {rec.zaloha_vyplacena_at && (
                                                            <button onClick={() => setOpravaId(rec.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4 }} title="Opravit výplatu (úvazek, záloha, ruční výplata)">
                                                                <Banknote size={13} />
                                                            </button>
                                                        )}
                                                        <button onClick={() => smazatZaznam(rec)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }} title="Smazat záznam">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    );
                })()}
            </div>

            {opravaId && (
                <OpravaVyplatyModal dochazkaId={opravaId} isDark={isDark} barva={SECTION_COLOR}
                    onClose={() => setOpravaId(null)} onUlozeno={() => { setOpravaId(null); fetchData(); }} />
            )}

            {/* Confirm modal — uzavření měsíce. Norma modalu (ModalShell): portál
                nad mobilní lištou, na mobilu sheet zdola. */}
            {closeModal && (
                <ModalShell onClose={() => setCloseModal(null)} zavritLze={!closing} isDark={isDark} sirka={480}
                    nadpis={`Uzavřít měsíc ${String(closeModal.mesic).padStart(2, '0')}/${closeModal.rok}?`}
                    ikona={Lock} barva={SECTION_COLOR}
                    paticka={(
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setCloseModal(null)} disabled={closing} style={{ padding: '8px 14px', borderRadius: 6, background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'var(--border-default)'}`, color: 'var(--text-secondary)', cursor: closing ? 'not-allowed' : 'pointer', fontSize: 13 }}>Zrušit</button>
                            <button onClick={submitClose} disabled={closing} style={{ padding: '8px 14px', borderRadius: 6, background: SECTION_COLOR, border: 'none', color: '#fff', cursor: closing ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Lock size={13} /> {closing ? 'Uzavírám…' : 'Uzavřít a odeslat'}
                            </button>
                        </div>
                    )}>
                    <div style={{ padding: 16 }}>
                        <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                            Tímto se měsíc uzamkne proti úpravám, vygeneruje se PDF za každého zaměstnance, vše se zazipuje a odešle účetnímu na email z konfigurace.
                            Záznamy s neukončenou směnou (clock_out je null) musí být před uzavřením vyřešené — jinak se akce odmítne.
                        </p>
                        {closeError && (
                            <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.1)', color: '#EF4444', fontSize: 12, marginBottom: 12 }}>
                                {closeError}
                            </div>
                        )}
                    </div>
                </ModalShell>
            )}

            {/* Reopen modal — superadmin only, povinný důvod */}
            {reopenModal && (
                <ModalShell onClose={() => setReopenModal(null)} zavritLze={!reopening} isDark={isDark} sirka={480}
                    nadpis={`Znovu otevřít ${String(reopenModal.mesic).padStart(2, '0')}/${reopenModal.rok}?`}
                    ikona={ShieldAlert} barva="#EF4444"
                    paticka={(
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setReopenModal(null)} disabled={reopening} style={{ padding: '8px 14px', borderRadius: 6, background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'var(--border-default)'}`, color: 'var(--text-secondary)', cursor: reopening ? 'not-allowed' : 'pointer', fontSize: 13 }}>Zrušit</button>
                            <button onClick={submitReopen} disabled={reopening} style={{ padding: '8px 14px', borderRadius: 6, background: '#EF4444', border: 'none', color: '#fff', cursor: reopening ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <ShieldAlert size={13} /> {reopening ? 'Otevírám…' : 'Znovu otevřít'}
                            </button>
                        </div>
                    )}>
                    <div style={{ padding: 16 }}>
                        <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                            Měsíc se odemkne pro úpravy. Důvod se zaznamená do auditu spolu s tvým ID a IP. ZIP archiv bude smazán — po opětovném uzavření se vygeneruje znovu.
                        </p>
                        <textarea value={reopenDuvod} onChange={e => setReopenDuvod(e.target.value)} placeholder="Důvod (min. 5 znaků) — např. Oprava chyby v hodinách u Petra, dohledána pauza."
                            rows={3}
                            style={{ ...iStyle, width: '100%', fontSize: 13, padding: 10, marginBottom: 12, resize: 'vertical' }} />
                        {reopenError && (
                            <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.1)', color: '#EF4444', fontSize: 12, marginBottom: 12 }}>
                                {reopenError}
                            </div>
                        )}
                    </div>
                </ModalShell>
            )}

            <style>{`@keyframes smena-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }`}</style>
        </div>
        {zobrazitPanel && (
            <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column',
                ...(plnaVyska ? { minHeight: 0 } : null) }}>
                {panelDochazky()}
            </div>
        )}
        </div>
    );
}
